/**
 * Menu — a button that opens a short list of actions, optionally grouped under
 * headings ("Assign to…"). Built on the shared Popover, so it is portalled,
 * viewport-clamped and dismisses on outside click / Escape.
 *
 * Presentational: the caller supplies labels and handlers. A section can be
 * `loading` (its items are still being fetched) or show `emptyLabel`.
 */
import { For, Show, children, type JSX } from "solid-js";
import ChevronDown from "lucide-solid/icons/chevron-down";
import { cn } from "./utils/cn";
import { focusRing, focusRingInset } from "./utils/focus-ring";
import { Popover } from "./popover";
import { Spinner } from "./spinner";

export interface MenuItem {
  id: string;
  label: string;
  /** Secondary text at the end of the row (e.g. "current"). */
  note?: string;
  icon?: JSX.Element;
  disabled?: boolean;
  onSelect: () => void;
}

export interface MenuSection {
  heading?: string;
  items: MenuItem[];
  loading?: boolean;
  /** Shown when the section has no items and is not loading. */
  emptyLabel?: string;
}

export function Menu(props: {
  /** Visible trigger label. */
  label: string;
  iconBefore?: JSX.Element;
  sections: MenuSection[];
  /** Fired each time the menu opens — fetch lazy sections here. */
  onOpen?: () => void;
  /** Spinner on the trigger while an action from the menu is running. */
  busy?: boolean;
  disabled?: boolean;
  align?: "start" | "end";
  width?: number;
  class?: string;
}): JSX.Element {
  const before = children(() => props.iconBefore);
  const shown = () =>
    props.sections.filter((s) => s.items.length > 0 || s.loading || !!s.emptyLabel);

  return (
    <Popover
      width={props.width ?? 240}
      align={props.align ?? "end"}
      class="py-075"
      trigger={({ toggle, open, setRef }) => (
        <button
          ref={setRef}
          type="button"
          disabled={props.disabled || props.busy}
          aria-haspopup="menu"
          aria-expanded={open()}
          aria-busy={props.busy ? "true" : undefined}
          onClick={() => {
            if (!open()) props.onOpen?.();
            toggle();
          }}
          class={cn(
            "c9-touch inline-flex h-6 shrink-0 items-center gap-075 whitespace-nowrap rounded-[3px] px-100",
            "bg-[var(--c9-bg-neutral)] text-body font-[570] text-[color:var(--c9-text)] transition-colors",
            "hover:bg-[var(--c9-bg-neutral-hovered)] active:bg-[var(--c9-bg-neutral-pressed)]",
            "disabled:cursor-not-allowed disabled:bg-[var(--c9-bg-disabled)] disabled:text-[color:var(--c9-text-disabled)]",
            focusRing,
            props.class,
          )}
        >
          <Show when={props.busy} fallback={before()}>
            <Spinner size="xsmall" />
          </Show>
          {props.label}
          <ChevronDown size={14} class="shrink-0" />
        </button>
      )}
    >
      {({ close }) => (
        <div role="menu" class="min-h-0 flex-1 overflow-y-auto">
          <For each={shown()}>
            {(section, i) => (
              <div
                class={cn(i() > 0 && "mt-075 border-t border-[color:var(--c9-border)] pt-075")}
              >
                <Show when={section.heading}>
                  <p class="px-150 py-050 text-[11px] font-[700] uppercase tracking-[0.04em] text-[color:var(--c9-text-subtlest)]">
                    {section.heading}
                  </p>
                </Show>
                <Show when={section.loading}>
                  <div class="flex items-center gap-100 px-150 py-075 text-body-s text-[color:var(--c9-text-subtlest)]">
                    <Spinner size="xsmall" /> Loading…
                  </div>
                </Show>
                <Show when={!section.loading && section.items.length === 0 && section.emptyLabel}>
                  <p class="px-150 py-075 text-body-s text-[color:var(--c9-text-subtlest)]">
                    {section.emptyLabel}
                  </p>
                </Show>
                <For each={section.items}>
                  {(item) => (
                    <button
                      type="button"
                      role="menuitem"
                      disabled={item.disabled}
                      onClick={() => {
                        close();
                        item.onSelect();
                      }}
                      class={cn(
                        "flex w-full items-center gap-100 px-150 py-075 text-left text-body text-[color:var(--c9-text)] transition-colors",
                        "hover:bg-[var(--c9-bg-neutral-subtle-hovered)]",
                        "disabled:cursor-not-allowed disabled:text-[color:var(--c9-text-disabled)] disabled:hover:bg-transparent",
                        focusRingInset,
                      )}
                    >
                      <Show when={item.icon}>
                        <span class="shrink-0 text-[color:var(--c9-icon-subtle)]">{item.icon}</span>
                      </Show>
                      <span class="min-w-0 flex-1 truncate">{item.label}</span>
                      <Show when={item.note}>
                        <span class="shrink-0 text-body-s text-[color:var(--c9-text-subtlest)]">
                          {item.note}
                        </span>
                      </Show>
                    </button>
                  )}
                </For>
              </div>
            )}
          </For>
        </div>
      )}
    </Popover>
  );
}
